import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Code2, Terminal, Users, Calendar, ArrowRight, Trophy, ChevronRight, ChevronLeft } from 'lucide-react';
export function ClubPage() {
  return <div className="bg-white min-h-screen text-slate-800 selection:bg-teal/20" data-id="element-634">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center pt-20 overflow-hidden bg-navy text-white" data-id="element-635">
        <div className="absolute inset-0 z-0" data-id="element-636">
          <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2070&auto=format&fit=crop" alt="Students coding" className="w-full h-full object-cover opacity-20 mix-blend-luminosity" data-id="element-637" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/95 to-navy/85" data-id="element-638" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto mt-16" data-id="element-639">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-640">
            <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-8" data-id="element-641">
              <Terminal className="w-4 h-4 text-white" data-id="element-642" />
              <span className="text-xs font-bold tracking-widest uppercase text-white" data-id="element-643">
                EWU Computer Programming Club
              </span>
            </div>

            <h1 className="text-6xl md:text-8xl font-extrabold tracking-tighter mb-6 leading-none text-white" data-id="element-644">
              Code. <br className="md:hidden" data-id="element-645" />
              <span className="text-gold" data-id="element-646">Collaborate.</span>{' '}
              <br className="md:hidden" data-id="element-647" />
              Conquer.
            </h1>

            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-12" data-id="element-648">
              Join the most active tech community on campus. We build, we hack,
              and we represent EWU on national and global stages.
            </p>

            <button className="bg-gold hover:bg-gold-hover text-white font-extrabold text-lg py-4 px-10 rounded-2xl transition-all shadow-lg hover:-translate-y-1" data-id="element-649">
              Become a Member
            </button>
          </motion.div>
        </div>
      </section>

      {/* Flagship Events (Asymmetrical Grid) */}
      <section className="py-24 max-w-7xl mx-auto px-6 relative z-10 bg-white" data-id="element-650">
        <div className="flex items-center mb-12" data-id="element-651">
          <Calendar className="w-8 h-8 text-teal mr-4" data-id="element-652" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-navy" data-id="element-653">
            Flagship Events
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-654">
          {/* Main Event Card (8 cols) */}
          <div className="lg:col-span-8 bg-white border border-slate-200 shadow-lg rounded-3xl p-8 md:p-12 relative overflow-hidden" data-id="element-655">
            <div className="relative z-10 flex flex-col h-full" data-id="element-656">
              <div className="bg-red-50 text-red-700 text-xs font-bold px-3 py-1 rounded-full w-fit mb-6 border border-red-200 flex items-center" data-id="element-657">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse mr-2" data-id="element-658" />{' '}
                Upcoming Major
              </div>

              <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-navy" data-id="element-659">
                Inter-University Programming Contest 2026
              </h3>
              <p className="text-slate-600 mb-8 max-w-xl" data-id="element-660">
                The biggest coding showdown of the year. Form your team of 3 and
                compete against the best minds from 50+ universities nationwide.
              </p>

              <CountdownTimer targetDate="2026-08-15T09:00:00" data-id="element-661" />

              <div className="mt-10 flex items-center space-x-4" data-id="element-662">
                <button className="bg-teal hover:bg-teal-hover text-white font-bold py-3 px-8 rounded-xl transition-colors shadow-sm" data-id="element-663">
                  Register Team
                </button>
                <button className="text-navy hover:text-gold font-medium py-3 px-6 transition-colors" data-id="element-664">
                  View Rulebook
                </button>
              </div>
            </div>
          </div>

          {/* Past Triumphs (4 cols) */}
          <div className="lg:col-span-4 flex flex-col gap-6" data-id="element-665">
            <div className="bg-white border border-slate-200 shadow-sm rounded-3xl p-6 flex-grow relative overflow-hidden group" data-id="element-666">
              <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=2070&auto=format&fit=crop" alt="Hackathon win" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" data-id="element-667" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-transparent" data-id="element-668" />

              <div className="relative z-10 h-full flex flex-col justify-end text-white" data-id="element-669">
                <Trophy className="w-8 h-8 text-gold mb-3" data-id="element-670" />
                <h4 className="text-xl font-bold mb-1" data-id="element-671">
                  Champions at NCPC 2025
                </h4>
                <p className="text-sm text-white/80" data-id="element-672">
                  Team 'EWU_Runtime_Terror' secured 1st place.
                </p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 shadow-sm rounded-3xl p-6 h-48 relative overflow-hidden group flex items-center justify-center cursor-pointer hover:border-navy transition-colors" data-id="element-673">
              <div className="text-center" data-id="element-674">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-navy group-hover:text-white transition-colors text-slate-500" data-id="element-675">
                  <ArrowRight className="w-5 h-5" data-id="element-676" />
                </div>
                <span className="font-bold text-slate-800 group-hover:text-navy" data-id="element-677">
                  View Hall of Fame
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Panel */}
      <section className="py-24 bg-slate-50 border-y border-slate-200 relative z-10" data-id="element-678">
        <div className="max-w-7xl mx-auto px-6" data-id="element-679">
          <div className="flex justify-between items-end mb-12" data-id="element-680">
            <div data-id="element-681">
              <div className="flex items-center mb-2" data-id="element-682">
                <Users className="w-6 h-6 text-teal mr-3" data-id="element-683" />
                <h2 className="text-3xl font-bold tracking-tight text-navy" data-id="element-684">
                  Executive Panel 2025-26
                </h2>
              </div>
              <p className="text-slate-500" data-id="element-685">
                The minds driving the community forward.
              </p>
            </div>
            <div className="hidden md:flex space-x-2" data-id="element-686">
              <button className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-100 transition-colors text-slate-600" data-id="element-687">
                <ChevronLeft className="w-5 h-5" data-id="element-688" />
              </button>
              <button className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-100 transition-colors text-slate-600" data-id="element-689">
                <ChevronRight className="w-5 h-5" data-id="element-690" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-691">
            <ProfileCard name="Fahim Rahman" role="President" img="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?q=80&w=1974&auto=format&fit=crop" data-id="element-692" />
            <ProfileCard name="Sadia Islam" role="Vice President" img="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=1964&auto=format&fit=crop" data-id="element-693" />
            <ProfileCard name="Tanvir Ahmed" role="General Secretary" img="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop" data-id="element-694" />
            <ProfileCard name="Nusrat Jahan" role="Event Coordinator" img="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?q=80&w=1964&auto=format&fit=crop" data-id="element-695" />
          </div>
        </div>
      </section>
    </div>;
}
function CountdownTimer({
  targetDate
}: {
  targetDate: string;
}) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const target = new Date(targetDate).getTime();
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = target - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          minutes: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(difference % (1000 * 60) / 1000)
        });
      } else {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);
  return <div className="flex space-x-4" data-id="element-696">
      <TimeUnit value={timeLeft.days} label="Days" data-id="element-697" />
      <div className="text-3xl font-bold text-slate-300 self-start mt-2" data-id="element-698">:</div>
      <TimeUnit value={timeLeft.hours} label="Hours" data-id="element-699" />
      <div className="text-3xl font-bold text-slate-300 self-start mt-2" data-id="element-700">:</div>
      <TimeUnit value={timeLeft.minutes} label="Mins" data-id="element-701" />
      <div className="text-3xl font-bold text-slate-300 self-start mt-2" data-id="element-702">:</div>
      <TimeUnit value={timeLeft.seconds} label="Secs" data-id="element-703" />
    </div>;
}
function TimeUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-704">
      <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-navy border border-navy flex items-center justify-center mb-2 shadow-sm" data-id="element-705">
        <span className="text-2xl md:text-4xl font-bold text-white font-mono" data-id="element-706">
          {value.toString().padStart(2, '0')}
        </span>
      </div>
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-707">
        {label}
      </span>
    </div>;
}
function ProfileCard({
  name,
  role,
  img
}: {
  name: string;
  role: string;
  img: string;
}) {
  return <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6 flex flex-col items-center text-center group hover:-translate-y-2 hover:shadow-lg transition-all duration-300" data-id="element-708">
      <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-slate-200 group-hover:border-gold transition-colors relative" data-id="element-709">
        <img src={img} alt={name} className="w-full h-full object-cover" data-id="element-710" />
      </div>
      <h4 className="font-bold text-lg text-navy" data-id="element-711">{name}</h4>
      <p className="text-teal text-sm font-medium" data-id="element-712">{role}</p>
    </div>;
}